import React, { useMemo, useState, useEffect } from 'react';
import SchoolPopup from './SchoolPopup';
import { CSP_NONCE } from '../../../shared/services/cspNonce';

interface SchoolDetailsTableProps {
  schools: any[];
  formatCellValue: (value: any, key?: string) => string;
  visibleColumns?: string[]; // Optional: specify which columns to show (controlled mode)
  defaultVisibleColumns?: string[]; // Optional: default columns to show initially (dynamic mode)
  maxHeight?: number; // Optional: max height in pixels (default: 700)
  selectedCategory?: string; // Category selected in parent
  selectedKPI?: string; // KPI field selected in parent
  selectedKPILabel?: string; // Human-readable KPI label
  columnLabelOverrides?: Record<string, string>; // Override column display labels { dataKey: 'Custom Label' }
  hiddenColumns?: string[]; // Columns to completely hide from table and dropdown
  // Note: filterContext and column pinning removed to keep component minimal
}

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

const SchoolDetailsTable: React.FC<SchoolDetailsTableProps> = ({
  schools,
  formatCellValue,
  visibleColumns,
  defaultVisibleColumns,
  maxHeight = 700,
  selectedCategory,
  selectedKPI,
  selectedKPILabel,
  columnLabelOverrides,
  hiddenColumns,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  // Local state to show SchoolPopup when a table row is clicked
  const [selectedSchoolForPopup, setSelectedSchoolForPopup] = useState<{ 
    schoolID: number | string; 
    schoolName?: string;
    rowData?: any; // Store the full row data
  } | null>(null);

  // Build columns and data dynamically based on school structure
  const { columns, tableData } = useMemo(() => {
    if (!schools || schools.length === 0) {
      return { columns: [], tableData: [] };
    }

    const firstSchool = schools[0];
    const allCols: Array<{ title: string; data: string; render?: (data: any) => string }> = [];
    const flatData: any[] = [];

    // Build column definitions from first school
    const buildColumns = (obj: any, prefix = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;

        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Nested object - recurse
          buildColumns(value, fullKey);
        } else {
          // Regular field - add column
          let displayName = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();

          // Convert "No Something" pattern to "No. of Something"
          if (displayName.startsWith('No ')) {
            displayName = displayName.replace(/^No /, 'No. of ');
          }

          allCols.push({
            title: displayName,
            data: fullKey,
            render: (data: any) => formatCellValue(data, fullKey)
          });
        }
      });
    };

    buildColumns(firstSchool);

    // Debug: Log available columns
    // console.log('SchoolDetailsTable - Available columns:', allCols.map(c => c.data));

    // Reorder columns with priority: School Name, State Name, RO Name, District, City (ID at end)
    const priorityOrder = [
      'schoolID',
      'schoolName',
      'class',
      'classID',
      'stateName',
      'roName',
      'districtName',
      'city',
      // Also check for nested versions (from schoolDetails)
      'schoolDetails.schoolID',
      'schoolDetails.schoolName',
      'schoolDetails.class',
      'schoolDetails.classID',
      'schoolDetails.stateName',
      'schoolDetails.roName',
      'schoolDetails.districtName',
      'schoolDetails.city',
    ];

    const reorderedCols: typeof allCols = [];
    const remainingCols: typeof allCols = [...allCols];

    // Place priority columns in order
    priorityOrder.forEach(priorityKey => {
      const index = remainingCols.findIndex(col => col.data === priorityKey);
      if (index !== -1) {
        const [col] = remainingCols.splice(index, 1);
        reorderedCols.push(col);
      }
    });

    // Separate 'id' column (exactly "id", not compound IDs like "schoolID", "roID", etc.) to place at the end
    const idCol: typeof allCols = [];
    const otherCols: typeof allCols = [];

    remainingCols.forEach(col => {
      // Only match exact 'id' or 'Id' field, not compound IDs
      if (col.data === 'id' || col.data === 'Id' || col.data === 'ID') {
        idCol.push(col);
      } else {
        otherCols.push(col);
      }
    });

    // Add remaining columns and 'id' column at the end
    reorderedCols.push(...otherCols);
    reorderedCols.push(...idCol);

    // Replace allCols with reordered version
    allCols.length = 0;
    allCols.push(...reorderedCols);

    // Remove hidden columns
    if (hiddenColumns && hiddenColumns.length > 0) {
      const hidden = new Set(hiddenColumns);
      const filtered = allCols.filter(col => !hidden.has(col.data));
      allCols.length = 0;
      allCols.push(...filtered);
    }

    // Apply column label overrides
    if (columnLabelOverrides) {
      allCols.forEach(col => {
        if (columnLabelOverrides[col.data]) {
          col.title = columnLabelOverrides[col.data];
        }
      });
    }

    // Filter columns based on visibleColumns prop or selectedColumns state
    const cols = visibleColumns
      ? allCols.filter(col => visibleColumns.includes(col.data))
      : allCols;

    // Flatten all schools data
    schools.forEach(school => {
      const flatSchool: any = {};

      const flattenObject = (obj: any, prefix = '') => {
        Object.entries(obj).forEach(([key, value]) => {
          const fullKey = prefix ? `${prefix}.${key}` : key;

          if (value && typeof value === 'object' && !Array.isArray(value)) {
            flattenObject(value, fullKey);
          } else {
            flatSchool[fullKey] = value;
          }
        });
      };

      flattenObject(school);
      flatData.push(flatSchool);
    });

    return { allColumns: allCols, columns: cols, tableData: flatData };
  }, [schools, formatCellValue, visibleColumns, columnLabelOverrides, hiddenColumns]);

  // Track the last defaultVisibleColumns to detect when they change
  const [lastDefaults, setLastDefaults] = useState<string>('');

  // Initialize selected columns on first render or when defaultVisibleColumns change
  useEffect(() => {
    if (columns.length > 0) {
      const defaultsKey = (defaultVisibleColumns || []).join(',');
      const defaultsChanged = defaultsKey !== lastDefaults;

      if (defaultsChanged) {
        setLastDefaults(defaultsKey);
      }

      setSelectedColumns(prevSelected => {
        // If defaults changed, always apply new defaults
        if (defaultsChanged) {
          if (defaultVisibleColumns && defaultVisibleColumns.length > 0) {
            const availableDefaults = defaultVisibleColumns.filter(colKey =>
              columns.some(col => col.data === colKey)
            );
            if (availableDefaults.length > 0) {
              return availableDefaults;
            }
          }
          return columns.map(col => col.data);
        }

        // If already has selections and defaults haven't changed, keep user's selection
        if (prevSelected.length > 0) {
          return prevSelected;
        }

        if (defaultVisibleColumns && defaultVisibleColumns.length > 0) {
          // Use default visible columns if provided
          const availableDefaults = defaultVisibleColumns.filter(colKey =>
            columns.some(col => col.data === colKey)
          );

          // If we have available defaults, use them
          if (availableDefaults.length > 0) {
            return availableDefaults;
          }
        }

        // Fall back to all available columns
        return columns.map(col => col.data);
      });
    }
  }, [columns, defaultVisibleColumns]); // Removed selectedColumns.length to prevent loop

  // Get visible columns based on user selection
  const visibleColumnsData = useMemo(() => {
    if (!visibleColumns) {
      // User can control visibility dynamically
      return columns.filter(col => selectedColumns.includes(col.data));
    }
    // If visibleColumns prop is provided, use it (controlled mode)
    return columns;
  }, [columns, selectedColumns, visibleColumns]);

  // Toggle column visibility
  const toggleColumn = (columnKey: string) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnKey)) {
        // Don't allow hiding all columns
        if (prev.length === 1) return prev;
        return prev.filter(key => key !== columnKey);
      } else {
        return [...prev, columnKey];
      }
    });
  };

  // Select all columns
  const selectAllColumns = () => {
    setSelectedColumns(columns.map(col => col.data));
  };

  // Deselect all columns (keep at least first column)
  const deselectAllColumns = () => {
    setSelectedColumns([columns[0]?.data].filter(Boolean));
  };

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return tableData;

    const query = searchQuery.toLowerCase();
    return tableData.filter(row => {
      return visibleColumnsData.some(col => {
        const value = row[col.data];
        const displayValue = col.render ? col.render(value) : String(value || '');
        return displayValue.toLowerCase().includes(query);
      });
    });
  }, [tableData, searchQuery, visibleColumnsData]);

  // Sort data based on sort config
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Try to parse as numbers for numeric sorting
      const aNum = Number(aValue);
      const bNum = Number(bValue);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  // Calculate totals for numeric columns (if needed in future)
  // const columnTotals = useMemo(() => {
  //   const totals: Record<string, number | string> = {};
  //   
  //   visibleColumnsData.forEach(col => {
  //     const values = sortedData.map(row => row[col.data]);
  //     const numericValues = values.filter(v => !isNaN(Number(v)) && v !== null && v !== undefined && v !== '');
  //     
  //     if (numericValues.length > 0 && numericValues.length === values.length) {
  //       // All values are numeric, calculate sum
  //       totals[col.data] = numericValues.reduce((sum, val) => sum + Number(val), 0);
  //     } else {
  //       totals[col.data] = '';
  //     }
  //   });
  //   
  //   return totals;
  // }, [visibleColumnsData, sortedData]);

  // Handle column sorting
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (current?.key === columnKey) {
        // Toggle direction
        if (current.direction === 'asc') {
          return { key: columnKey, direction: 'desc' };
        } else {
          return null; // Remove sorting
        }
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  // Export to CSV (simple headers + rows)
  const exportToCSV = () => {
    const headers = ['S.No.', ...visibleColumnsData.map(col => col.title)].join(',');
    const rows = sortedData.map((row, idx) => {
      const sno = `"${idx + 1}"`;
      const rowData = visibleColumnsData.map(col => {
        const value = row[col.data];
        const displayValue = col.render ? col.render(value) : String(value || '');
        return `"${displayValue.replace(/"/g, '""')}"`;
      }).join(',');
      return [sno, rowData].join(',');
    });

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `school_details_${new Date().getTime()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setShowDownloadMenu(false);
  };

  // Export to Excel (TSV) - simple headers + rows
  const exportToExcel = () => {
    const headers = ['S.No.', ...visibleColumnsData.map(col => col.title)].join('\t');
    const rows = sortedData.map((row, idx) => {
      const sno = String(idx + 1);
      const rowData = visibleColumnsData.map(col => {
        const value = row[col.data];
        return col.render ? col.render(value) : String(value || '');
      }).join('\t');
      return [sno, rowData].join('\t');
    });

    const tsv = [headers, ...rows].join('\n');
    const blob = new Blob([tsv], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `school_details_${new Date().getTime()}.xls`;
    a.click();
    window.URL.revokeObjectURL(url);
    setShowDownloadMenu(false);
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    const headers = ['S.No.', ...visibleColumnsData.map(col => col.title)].join('\t');
    const rows = sortedData.map((row, idx) => {
      const sno = String(idx + 1);
      const rowData = visibleColumnsData.map(col => {
        const value = row[col.data];
        return col.render ? col.render(value) : String(value || '');
      }).join('\t');
      return [sno, rowData].join('\t');
    });

    const text = [headers, ...rows].join('\n');
    navigator.clipboard.writeText(text).then(() => {
      alert('Data copied to clipboard!');
      setShowDownloadMenu(false);
    });
  };

  // Handle empty state
  if (!schools || schools.length === 0) {
    return <div className="text-center py-4 text-gray-400">No school details available</div>;
  }

  // Calculate table container height dynamically based on content
  const ROW_HEIGHT = 52;
  const HEADER_HEIGHT = 60;
  const FOOTER_HEIGHT = 56;
  const CONTROL_BAR_HEIGHT = 68; // 52px control bar + 16px margin

  // Calculate expected content height
  let contentHeight = HEADER_HEIGHT + (sortedData.length * ROW_HEIGHT);
  if (sortedData.length > 0) {
    contentHeight += FOOTER_HEIGHT;
  }

  // Clamp height between 200px and maxHeight
  const tableContainerHeight = Math.min(Math.max(contentHeight, 200), maxHeight);
  const totalHeight = tableContainerHeight + CONTROL_BAR_HEIGHT;

  return (
    <div
      style={{
        width: '100%',
        height: `${totalHeight}px`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <style nonce={CSP_NONCE}>{`
        /* Scrollable table container - COMPLETELY ISOLATED */
        .school-details-table-wrapper {
          position: absolute;
          top: 52px; /* Below control bar */
          left: 0;
          right: 0;
          bottom: 0;
          overflow: auto;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          background: white;
        }
        
        /* Table - grows horizontally, creates scroll */
        .school-details-table-wrapper table {
          width: max-content;
          min-width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }
        
        .school-details-table-wrapper thead {
          position: sticky;
          top: 0;
          z-index: 10;
          background: linear-gradient(90deg, #481789 0%, #6B1786 100%);
        }
        
        .school-details-table-wrapper th {
          background: linear-gradient(90deg, #481789 0%, #6B1786 100%) !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          font-size: 0.875rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.025em !important;
          padding: 1rem !important;
          border-bottom: 2px solid #6B1786 !important;
          text-align: left !important;
          white-space: nowrap;
          border-right: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .school-details-table-wrapper th:hover {
          background: linear-gradient(90deg, #5a1f9b 0%, #7d1f98 100%) !important;
        }
        
        .school-details-table-wrapper th:last-child {
          border-right: none;
        }
        
        .school-details-table-wrapper td {
          padding: 0.875rem 1rem !important;
          border-bottom: 1px solid #f3f4f6 !important;
          color: #1f2937 !important;
          font-size: 0.875rem !important;
          vertical-align: middle !important;
          border-right: 1px solid #f3f4f6;
        }
        
        .school-details-table-wrapper td:last-child {
          border-right: none;
        }
        
        .school-details-table-wrapper tbody tr:hover {
          background-color: #f9fafb !important;
        }
        
        .school-details-table-wrapper tbody tr:last-child td {
          border-bottom: none !important;
        }
        
        /* Scrollbar styling */
        .school-details-table-wrapper::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        
        .school-details-table-wrapper::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        
        .school-details-table-wrapper::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        
        .school-details-table-wrapper::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        
        .school-details-table-wrapper::-webkit-scrollbar-corner {
          background: #f1f5f9;
        }
      `}</style>

      {/* Filter and Download Controls */}
      <div className="school-details-control-bar flex justify-between items-center mb-4 gap-4">
        <div className="text-sm text-gray-600">
          Showing {sortedData.length} of {tableData.length} schools
        </div>

        <div className="flex items-center gap-3">
          {/* Search/Filter Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search schools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-64"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* Download Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download
              <svg className={`w-4 h-4 transition-transform ${showDownloadMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showDownloadMenu && (
              <>
                {/* Backdrop to close menu */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowDownloadMenu(false)}
                />

                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  <button
                    onClick={copyToClipboard}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy to Clipboard
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download CSV
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Download Excel
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Column Visibility Dropdown - Only show if not using visibleColumns prop */}
          {!visibleColumns && (
            <div className="relative">
              <button
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Columns ({selectedColumns.length})
                <svg className={`w-4 h-4 transition-transform ${showColumnMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showColumnMenu && (
                <>
                  {/* Backdrop to close menu */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowColumnMenu(false)}
                  />

                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20 max-h-96 overflow-y-auto">
                    {/* Select All / Deselect All buttons */}
                    <div className="px-3 pb-2 mb-2 border-b border-gray-200 flex gap-2">
                      <button
                        onClick={selectAllColumns}
                        className="flex-1 px-3 py-1.5 text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 rounded transition-colors"
                      >
                        Select All
                      </button>
                      <button
                        onClick={deselectAllColumns}
                        className="flex-1 px-3 py-1.5 text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 rounded transition-colors"
                      >
                        Deselect All
                      </button>
                    </div>

                    {/* Column checkboxes */}
                    <div className="max-h-64 overflow-y-auto">
                      {columns.map((col, idx) => (
                        <label
                          key={idx}
                          className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(col.data)}
                            onChange={() => toggleColumn(col.data)}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {col.title}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="school-details-table-wrapper">
        <table className="bg-white">
          <thead>
            <tr>
              <th className="cursor-default select-none transition-colors" style={{ width: '60px' }}>
                <div className="flex items-center justify-center">
                  <span>S.No.</span>
                </div>
              </th>
              {visibleColumnsData.map((col, idx) => {
                return (
                  <th
                    key={idx}
                    className={`cursor-pointer select-none transition-colors`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1" onClick={() => handleSort(col.data)}>
                        <span>{col.title}</span>
                        <span className="flex flex-col">
                          {sortConfig?.key === col.data ? (
                            sortConfig.direction === 'asc' ? (
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            )
                          ) : (
                            <svg className="w-4 h-4 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                            </svg>
                          )}
                        </span>
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, rowIdx) => {
              // derive possible school id/name from flattened row
              const possibleId = row['schoolID'] || row['school_id'] || row['id'] || row['schoolDetails.schoolID'] || row['schoolDetails.school_id'];
              const possibleName = row['schoolName'] || row['school_name'] || row['schoolDetails.schoolName'] || String(possibleId || '');

              return (
                <tr
                  key={rowIdx}
                  className={`${rowIdx % 2 === 0 ? 'bg-[#f7e3f2]' : 'bg-[#cac6cc]'} cursor-pointer`}
                  title="Click to view school details"
                  onClick={() => {
                    if (possibleId) {
                      setSelectedSchoolForPopup({ 
                        schoolID: possibleId, 
                        schoolName: possibleName,
                        rowData: schools[rowIdx] // Store the original school data (not flattened)
                      });
                    }
                  }}
                >
                  <td className="text-center">{rowIdx + 1}</td>
                  {visibleColumnsData.map((col, colIdx) => {
                    return (
                      <td key={colIdx}>
                        {col.render ? col.render(row[col.data]) : row[col.data]}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {sortedData.length === 0 && (
              <tr>
                <td colSpan={visibleColumnsData.length + 1} className="text-center py-8 text-gray-400">
                  No results found for "{searchQuery}"
                </td>
              </tr>
            )}
          </tbody>
          {sortedData.length > 0 && (
            <tfoot>
              <tr className="bg-purple-50 font-semibold border-t-2 border-purple-200">
                <td className="px-4 py-3 text-gray-800">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span>Total Schools</span>
                  </div>
                </td>
                <td colSpan={Math.max(1, visibleColumnsData.length)} className="px-4 py-3 text-purple-700 font-semibold text-right">
                  {sortedData.length}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
        {/* School popup opened when a table row is clicked */}
        {selectedSchoolForPopup && (
          <SchoolPopup
            schoolID={Number(selectedSchoolForPopup.schoolID)}
            schoolName={selectedSchoolForPopup.schoolName || String(selectedSchoolForPopup.schoolID)}
            onClose={() => setSelectedSchoolForPopup(null)}
            onViewFullDetails={() => { /* noop - SchoolPopup handles navigation itself */ }}
            selectedKPI={
              selectedCategory && selectedKPI
                ? {
                    category: selectedCategory,
                    kpiField: selectedKPI,
                    kpiLabel: selectedKPILabel,
                    kpiValue: selectedSchoolForPopup.rowData?.[selectedKPI],
                  }
                : undefined
            }
            schoolKPIData={selectedSchoolForPopup.rowData}
          />
        )}
      </div>
  );
};

export default SchoolDetailsTable;
